import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { IUserPrefSession } from "../ITypes";
import { CacheService } from "./cache.service";
import { AppConfig } from "src/app/config";

@Injectable({
  providedIn: "root",
})
export class UserPrefSessionService {
  private readonly USER_PREF_KEY = AppConfig.userPrefKey;
  private userPrefSubject: BehaviorSubject<IUserPrefSession | null> =
    new BehaviorSubject<IUserPrefSession | null>(null);

  /**
   * Observable for user preferences changes
   */
  public userPref$: Observable<IUserPrefSession | null> =
    this.userPrefSubject.asObservable();

  constructor(private cacheService: CacheService) {
    // Initialize from local storage
    this.initFromStorage();
  }

  /**
   * Initialize user preferences from local storage
   */
  private initFromStorage(): void {
    try {
      const storedPrefs = this.cacheService.get(this.USER_PREF_KEY);
      if (storedPrefs) {
        this.userPrefSubject.next(storedPrefs);
      }
    } catch (error) {
      console.error("Error loading user preferences from storage:", error);
    }
  }

  /**
   * Get current user preferences from memory
   * @returns Current user preferences or null if not set
   */
  getUserPreferences(): IUserPrefSession | null {
    return this.userPrefSubject.getValue();
  }

  /**
   * Get user preferences from local storage
   * @returns User preferences from storage or null if not found
   */
  getUserPreferencesFromStorage(): IUserPrefSession | null {
    try {
      return this.cacheService.get(this.USER_PREF_KEY);
    } catch (error) {
      console.error("Error getting user preferences from storage:", error);
      return null;
    }
  }

  /**
   * Set user preferences in memory and local storage
   * @param userPref User preferences to store
   */
  setUserPreferences(userPref: IUserPrefSession): void {
    try {
      // Store in local storage
      this.cacheService.set(this.USER_PREF_KEY, userPref);

      // Update in-memory value and notify subscribers
      this.userPrefSubject.next(userPref);
    } catch (error) {
      console.error("Error setting user preferences:", error);
    }
  }

  /**
   * Update specific fields in user preferences
   * @param prefUpdates Partial user preferences to update
   * @returns Updated user preferences or null if operation failed
   */
  updateUserPreferences(
    prefUpdates: Partial<IUserPrefSession>
  ): IUserPrefSession | null {
    try {
      const currentPrefs = this.getUserPreferences();

      // If no current preferences, create new ones
      if (!currentPrefs) {
        if (!prefUpdates.user_uuid) {
          console.error("Cannot create new preferences without user_uuid");
          return null;
        }

        const newPrefs: IUserPrefSession = {
          user_uuid: prefUpdates.user_uuid,
          last_channel_id: prefUpdates.last_channel_id || null,
        };

        this.setUserPreferences(newPrefs);
        return newPrefs;
      }

      // Update existing preferences
      const updatedPrefs: IUserPrefSession = {
        ...currentPrefs,
        ...prefUpdates,
      };

      this.setUserPreferences(updatedPrefs);
      return updatedPrefs;
    } catch (error) {
      console.error("Error updating user preferences:", error);
      return null;
    }
  }

  /**
   * Set the last selected channel ID
   * @param channelId Channel ID to save
   * @returns Updated user preferences or null if operation failed
   */
  setLastChannelId(channelId: number): IUserPrefSession | null {
    return this.updateUserPreferences({ last_channel_id: channelId });
  }

  /**
   * Get the last selected channel ID
   * @returns Last channel ID or null if not set
   */
  getLastChannelId(): number | null {
    return this.getUserPreferences()?.last_channel_id || null;
  }

  /**
   * Clear user preferences from memory and local storage
   */
  clearUserPreferences(): void {
    try {
      this.cacheService.remove(this.USER_PREF_KEY);
      this.userPrefSubject.next(null);
    } catch (error) {
      console.error("Error clearing user preferences:", error);
    }
  }

  /**
   * Check if the stored user UUID matches the current user UUID
   * If not, clear the preferences and create new ones for the current user
   * @param currentUserUuid Current user UUID
   * @param channelId Optional channel ID to set for the new user
   * @returns Updated user preferences or null if operation failed
   */
  ensureUserMatch(
    currentUserUuid: string,
    channelId?: number
  ): IUserPrefSession | null {
    try {
      const storedPrefs = this.getUserPreferences();

      // If no stored preferences or user UUID doesn't match, create new preferences
      if (!storedPrefs || storedPrefs.user_uuid !== currentUserUuid) {
        console.log(
          "User UUID changed or no preferences found, creating new preferences"
        );

        // Clear any existing preferences
        this.clearUserPreferences();

        // Create new preferences for the current user
        const newPrefs: IUserPrefSession = {
          user_uuid: currentUserUuid,
          last_channel_id: channelId || null,
        };

        this.setUserPreferences(newPrefs);
        return newPrefs;
      }

      // If user UUID matches but channel ID is provided and different, update it
      if (channelId && storedPrefs.last_channel_id !== channelId) {
        return this.setLastChannelId(channelId);
      }

      return storedPrefs;
    } catch (error) {
      console.error("Error ensuring user match:", error);
      return null;
    }
  }

  /**
   * Get the last channel ID if the user UUID matches the provided UUID
   * @param userUuid User UUID to check against stored preferences
   * @returns Last channel ID if user UUID matches, otherwise null
   */
  getLastChannelIdIfUserMatch(userUuid: string): number | null {
    try {
      const storedPrefs = this.getUserPreferences();

      // If preferences exist and user UUID matches, return the last channel ID
      if (storedPrefs && storedPrefs.user_uuid === userUuid) {
        return storedPrefs.last_channel_id;
      }

      // Return null if no preferences or user UUID doesn't match
      return null;
    } catch (error) {
      console.error("Error checking user match for channel ID:", error);
      return null;
    }
  }

  getLastPreferenceSession(userUuid: string): IUserPrefSession | null {
    try {
      const storedPrefs = this.getUserPreferences();

      if (storedPrefs && storedPrefs.user_uuid === userUuid) {
        return storedPrefs;
      }

      return null;
    } catch (error) {
      return null;
    }
  }
}
